import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { employeeAPI, attendanceAPI, leaveAPI } from '../services/api';

const fmt = n => n ? new Intl.NumberFormat('vi-VN').format(Math.round(n)) : '0';

export default function EmployeeDetailPage() {
  const { id } = useParams(); // slug param ignored — id is used for data lookup
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [tab, setTab] = useState('info');
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
    Promise.all([
      employeeAPI.get(id),
      attendanceAPI.monthly(id, new Date().toISOString().substring(0, 7)),
      leaveAPI.balance(id, new Date().getFullYear()),
    ]).then(([empRes, attRes, balRes]) => {
      setEmp(empRes.data.data);
      setAttendance(attRes.data.data);
      setLeaveBalance(balRes.data.data);
    }).catch(err => setError(err.response?.data?.message || 'Không thể tải dữ liệu nhân viên'));
  }, [id]);

  if (error) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>
      ⚠️ {error}
      <div style={{ marginTop: 12 }}>
        <button onClick={() => navigate('/employees')}
          style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: '#fff', fontSize: 13 }}>
          ← Quay lại danh sách
        </button>
      </div>
    </div>
  );
  if (!emp) return <div style={{ padding: 40, textAlign: 'center' }}>⏳ Đang tải...</div>;

  const tabs = ['info', 'salary', 'attendance', 'leave'];
  const tabLabels = { info: '📋 Thông tin', salary: '💰 Lương', attendance: '⏱️ Chấm công', leave: '📅 Nghỉ phép' };

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 13, color: '#64748b' }}>
        <button onClick={() => navigate('/employees')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: 13, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          ← Nhân viên
        </button>
        <span style={{ color: '#cbd5e1' }}>/</span>
        <span style={{ fontWeight: 600, color: '#1e293b' }}>{emp.first_name} {emp.last_name}</span>
      </div>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg,#1e3a8a,#0891b2)', borderRadius: 16, padding: 28, color: '#fff', marginBottom: 20, display: 'flex', gap: 24, alignItems: 'center' }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%',
          background: 'rgba(255,255,255,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 32, fontWeight: 700, flexShrink: 0
        }}>
          {emp.first_name.charAt(0)}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{emp.first_name} {emp.last_name}</div>
          <div style={{ opacity: 0.85, fontSize: 14 }}>{emp.position_title} • {emp.department_name}</div>
          <div style={{ opacity: 0.7, fontSize: 13, marginTop: 4 }}>{emp.employee_code} • {emp.email}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(emp.base_salary)} ₫</div>
          <div style={{ opacity: 0.8, fontSize: 13 }}>Lương cơ bản</div>
          <span style={{
            display: 'inline-block', marginTop: 8, padding: '4px 12px',
            background: emp.status === 'active' ? '#22c55e' : '#ef4444',
            borderRadius: 12, fontSize: 12, fontWeight: 600
          }}>{emp.status === 'active' ? 'Đang làm việc' : emp.status}</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: '#f1f5f9', padding: 4, borderRadius: 10, width: 'fit-content' }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 18px', border: 'none', borderRadius: 8, cursor: 'pointer',
            background: tab === t ? '#fff' : 'transparent',
            color: tab === t ? '#1e3a8a' : '#64748b',
            fontWeight: tab === t ? 600 : 400, fontSize: 13,
            boxShadow: tab === t ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
          }}>{tabLabels[t]}</button>
        ))}
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        {tab === 'info' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {[
              ['Mã nhân viên', emp.employee_code],
              ['Họ tên', `${emp.first_name} ${emp.last_name}`],
              ['Email', emp.email],
              ['Điện thoại', emp.phone],
              ['Giới tính', emp.gender === 'male' ? 'Nam' : 'Nữ'],
              ['Ngày sinh', emp.dob?.substring(0,10)],
              ['Phòng ban', emp.department_name],
              ['Chức vụ', emp.position_title],
              ['Quản lý', emp.manager_name || 'Không có'],
              ['Ngày vào làm', emp.join_date?.substring(0,10)],
              ['Loại HĐ', emp.contract_type],
              ['Đơn vị', emp.entity_name],
            ].map(([label, value]) => (
              <div key={label} style={{ padding: '12px 0', borderBottom: '1px solid #f8fafc' }}>
                <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 500 }}>{value || '—'}</div>
              </div>
            ))}
          </div>
        )}

        {tab === 'salary' && (
          <div>
            <h3 style={{ margin: '0 0 20px', fontSize: 16, color: '#1e293b' }}>Cấu trúc lương</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[
                { label: 'Lương cơ bản', value: emp.base_salary, color: '#1e3a8a' },
                { label: 'PC Ăn ca', value: emp.allowance_meal, color: '#0891b2' },
                { label: 'PC Xăng xe', value: emp.allowance_transport, color: '#0891b2' },
                { label: 'PC Điện thoại', value: emp.allowance_phone, color: '#0891b2' },
                { label: 'PC Nhà ở', value: emp.allowance_housing, color: '#0891b2' },
                { label: 'PC Trách nhiệm', value: emp.allowance_responsibility, color: '#0891b2' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ padding: 16, background: '#f8fafc', borderRadius: 10 }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>{label}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color }}>{fmt(value)} ₫</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 20, padding: 16, background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>TỔNG THU NHẬP (GROSS)</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#1e3a8a' }}>
                    {fmt((emp.base_salary||0)+(emp.allowance_meal||0)+(emp.allowance_transport||0)+(emp.allowance_phone||0)+(emp.allowance_housing||0)+(emp.allowance_responsibility||0))} ₫
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Lương đóng BHXH</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#1e293b' }}>{fmt(emp.bhxh_base)} ₫</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'attendance' && (
          <div>
            <h3 style={{ margin: '0 0 16px' }}>Chấm công tháng {new Date().getMonth()+1}/{new Date().getFullYear()}</h3>
            {attendance.length === 0 ? (
              <p style={{ color: '#94a3b8' }}>Chưa có dữ liệu chấm công</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Ngày', 'Check-in', 'Check-out', 'Giờ làm', 'OT', 'Trạng thái'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {attendance.map(a => (
                    <tr key={a.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '8px 12px' }}>{a.work_date?.substring(0,10)}</td>
                      <td style={{ padding: '8px 12px' }}>{a.check_in ? new Date(a.check_in).toLocaleTimeString('vi-VN', {hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                      <td style={{ padding: '8px 12px' }}>{a.check_out ? new Date(a.check_out).toLocaleTimeString('vi-VN', {hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                      <td style={{ padding: '8px 12px' }}>{a.worked_hours}h</td>
                      <td style={{ padding: '8px 12px' }}>{a.ot_hours > 0 ? `${a.ot_hours}h` : '—'}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                          background: a.status === 'present' ? '#dcfce7' : a.status === 'late' ? '#fef9c3' : '#fee2e2',
                          color: a.status === 'present' ? '#16a34a' : a.status === 'late' ? '#ca8a04' : '#dc2626',
                        }}>{a.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === 'leave' && (
          <div>
            <h3 style={{ margin: '0 0 16px' }}>Số dư nghỉ phép năm {new Date().getFullYear()}</h3>
            {leaveBalance.length === 0 ? (
              <p style={{ color: '#94a3b8' }}>Chưa có dữ liệu</p>
            ) : leaveBalance.map(bal => (
              <div key={bal.id} style={{ background: '#f8fafc', borderRadius: 10, padding: 20, marginBottom: 12 }}>
                <div style={{ fontWeight: 600, color: '#1e3a8a', marginBottom: 12, textTransform: 'capitalize' }}>{bal.leave_type} leave</div>
                <div style={{ display: 'flex', gap: 24 }}>
                  {[['Tổng', bal.total_days, '#1e3a8a'], ['Đã dùng', bal.used_days, '#ef4444'], ['Đang chờ', bal.pending_days, '#f59e0b'], ['Còn lại', bal.total_days - bal.used_days - bal.pending_days, '#22c55e']].map(([l, v, c]) => (
                    <div key={l}>
                      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{l}</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: c }}>{v}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>ngày</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 12, background: '#e2e8f0', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                  <div style={{ width: `${((bal.used_days / bal.total_days) * 100)}%`, background: '#1e3a8a', height: '100%', borderRadius: 4 }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
